import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin } from 'lucide-react';
import { africanCountries } from '../data/locationData';

/**
 * Manual Location Entry Component for Admin
 * Used when admin creates athlete profiles manually (GPS not auto-captured)
 */
const ManualLocationEntry = ({ 
    location, 
    onLocationChange,
    className = '' 
}) => {
    const [showCoordinates, setShowCoordinates] = useState(false);

    const handleChange = (field, value) => {
        onLocationChange({
            ...location,
            [field]: value,
            timestamp: new Date().toISOString()
        });
    };

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-primary" />
                <Label className="text-xs tracking-widest uppercase text-muted-foreground">
                    Athlete Location (Optional)
                </Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Country</Label>
                    <Select 
                        value={location?.country || ''} 
                        onValueChange={(v) => handleChange('country', v)}
                    >
                        <SelectTrigger className="bg-[#0a1628] border-white/10 h-10">
                            <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10 max-h-60">
                            {africanCountries.map((country) => (
                                <SelectItem key={country} value={country}>
                                    {country}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">City/Town</Label>
                    <Input
                        value={location?.city || ''}
                        onChange={(e) => handleChange('city', e.target.value)}
                        className="bg-[#0a1628] border-white/10 h-10"
                        placeholder="e.g., Nairobi, Lagos"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">State/Region</Label>
                <Input
                    value={location?.state || ''}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className="bg-[#0a1628] border-white/10 h-10"
                    placeholder="e.g., Nairobi County, Lagos State"
                />
            </div>

            <button
                type="button"
                onClick={() => setShowCoordinates(!showCoordinates)}
                className="text-xs text-primary hover:underline"
            >
                {showCoordinates ? 'Hide coordinates' : 'Add GPS coordinates (optional)'}
            </button>

            {showCoordinates && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Latitude</Label>
                        <Input
                            type="number"
                            step="0.000001"
                            value={location?.latitude || ''}
                            onChange={(e) => handleChange('latitude', parseFloat(e.target.value) || null)}
                            className="bg-[#0a1628] border-white/10 h-10"
                            placeholder="e.g., -1.2921"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Longitude</Label>
                        <Input
                            type="number"
                            step="0.000001"
                            value={location?.longitude || ''}
                            onChange={(e) => handleChange('longitude', parseFloat(e.target.value) || null)}
                            className="bg-[#0a1628] border-white/10 h-10"
                            placeholder="e.g., 36.8219"
                        />
                    </div>
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                Location helps scouts discover athletes in specific regions.
            </p>
        </div>
    );
};

export default ManualLocationEntry;
